// src/components/drillholes/DrillholesApp.tsx

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DrillholesDataPanel } from './data_panel/DrillholesDataPanel';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DrillholesSubNav  = 'holes' | 'analysis';
export type EntityId          = 'collars' | 'surveys' | 'drillholes';
export type CollarSubView     = 'coordinates' | 'sources' | '3d';
export type SurveySubView     = 'stations'    | 'sources' | '3d';

const ENTITY_LABELS: Record<EntityId, string> = {
  collars:    'Collars',
  surveys:    'Surveys',
  drillholes: 'Drillholes',
};

const COLLAR_SUB_LABELS: Record<CollarSubView, string> = {
  coordinates: 'Coordinates',
  sources:     'Source files',
  '3d':        '3D view',
};

const SURVEY_SUB_LABELS: Record<SurveySubView, string> = {
  stations: 'Stations',
  sources:  'Source files',
  '3d':     '3D view',
};

// ── Analysis placeholder ──────────────────────────────────────────────────────

function AnalysisPanel() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-center">
        <span className="inline-block rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
          Premium
        </span>
        <p className="mt-3 text-[15px] font-medium text-muted-foreground">
          Geotechnical Parameters
        </p>
        <p className="mt-1 text-[12px] text-muted-foreground/60">
          classification systems · RQD · strength analysis
        </p>
      </div>
    </div>
  );
}

// ── Mobile dropdown ───────────────────────────────────────────────────────────

function MobileDropdown<T extends string>({
  value, labels, onChange,
}: {
  value: T;
  labels: Record<T, string>;
  onChange: (v: T) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 text-sm font-medium text-foreground border-b border-foreground/30 hover:border-foreground/70 pb-px transition-colors">
          {labels[value]}
          <ChevronDown size={13} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        {(Object.keys(labels) as T[]).map((id) => (
          <DropdownMenuItem
            key={id}
            onClick={() => onChange(id)}
            className="flex items-center justify-between"
          >
            {labels[id]}
            {value === id && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

interface DrillholesAppProps {
  subNav: DrillholesSubNav;
}

export function DrillholesApp({ subNav }: DrillholesAppProps) {
  const [activeEntity,   setActiveEntity]   = useState<EntityId>('collars');
  const [collarSubView,  setCollarSubView]  = useState<CollarSubView>('coordinates');
  const [surveySubView,  setSurveySubView]  = useState<SurveySubView>('stations');

  // Which sub-label + setter to show for the active entity
  // const subLabel = activeEntity === 'collars'
  //   ? COLLAR_SUB_LABELS[collarSubView]
  //   : activeEntity === 'surveys'
  //     ? SURVEY_SUB_LABELS[surveySubView]
  //     : null;

  return (
    <div className="flex flex-col flex-1 min-h-0">

      {/* Top bar */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-4">

        {/* Title — always visible */}
        <span className="text-[15px] font-medium text-foreground">Drillholes</span>
        <span className="text-[14px] text-muted-foreground">
          · {subNav[0].toUpperCase() + subNav.slice(1)}
        </span>

        {/* Mobile controls — hidden on md+ */}
        {subNav === 'holes' && (
          <div className="ml-auto flex items-center gap-3 md:hidden">

            {/* Entity dropdown */}
            <MobileDropdown
              value={activeEntity}
              labels={ENTITY_LABELS}
              onChange={setActiveEntity}
            />

            {/* Sub-view dropdown — only when entity has sub-views */}
            {activeEntity === 'collars' && (
              <>
                <span className="text-muted-foreground/40 text-xs">·</span>
                <MobileDropdown
                  value={collarSubView}
                  labels={COLLAR_SUB_LABELS}
                  onChange={setCollarSubView}
                />
              </>
            )}
            {activeEntity === 'surveys' && (
              <>
                <span className="text-muted-foreground/40 text-xs">·</span>
                <MobileDropdown
                  value={surveySubView}
                  labels={SURVEY_SUB_LABELS}
                  onChange={setSurveySubView}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className={subNav === 'holes'     ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
        <DrillholesDataPanel
          activeEntity={activeEntity}
          onEntityChange={setActiveEntity}
          collarSubView={collarSubView}
          onCollarSubViewChange={setCollarSubView}
          surveySubView={surveySubView}
          onSurveySubViewChange={setSurveySubView}
        />
      </div>
      <div className={subNav === 'analysis' ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
        <AnalysisPanel />
      </div>

    </div>
  );
}